<script setup lang="ts">
import { computed, ref } from 'vue';
import { standardsForRegion } from './standardsAndSources';
import { zendeskCardholderSearchUrl } from './zendeskSearchUrl';

const props = withDefaults(
  defineProps<{
    subjectName: string;
    subjectAddress: string;
    focusedRecordUrl?: string;
    reviewRegion: string;
    accountId?: string;
    // 'panel' is the persistent desktop aside (fixed width, its own
    // border, hidden below `lg`). 'sheet' is the same Tools/Reference
    // content hosted inside the mobile bottom sheet instead — no fixed
    // width/border of its own (the sheet wrapper provides that), and no
    // number-key badges, since number keys do nothing without a keyboard.
    variant?: 'panel' | 'sheet';
    // Matches the app bar's own Keyboard switch — v4 hides every keycap
    // badge (this one included) the instant shortcuts are off, not just
    // the ones under `sheet`'s always-no-keyboard case.
    kbEnabled?: boolean;
    // Shown on the mobile-only Documents tab's label, matching the
    // `Records ${n}` convention AmlReviewCasePage.vue's own mobile tabs
    // already use. Undefined just shows "Documents" with no count.
    documentsCount?: number;
  }>(),
  { variant: 'panel', kbEnabled: true }
);

// Google and LinkedIn block iframe embedding via X-Frame-Options, and none
// of these load live inside the docked pane — each opens in a new tab
// instead. Maps/LinkedIn/Wayback pre-compose a query from whatever's in view
// (the subject, or the currently focused hit record's source); Google and
// Gemini are bare destinations for now, not a subject-name search — the
// user asked for Google specifically, and Gemini's "gems" landing page has
// nothing case-specific to compose from either. `compose` names this
// honestly per tool. No separate pre-filled/blank badge — checked directly
// against the real v4 source, which has no such badge, just this
// description line. VPN (a sandboxed exit-node browser) and Registry (a
// jurisdiction-specific company-search integration) are dropped for now —
// neither has a real destination to link to yet.
type ToolKey = 'google' | 'gemini' | 'wayback' | 'maps' | 'linkedin' | 'zendesk';

interface Tool {
  key: ToolKey;
  name: string;
  url: string;
  compose: string;
}

const TOOLS = computed<Tool[]>(() => {
  const nameQuery = encodeURIComponent(props.subjectName);
  const addressQuery = encodeURIComponent(props.subjectAddress);
  const tools: Tool[] = [
    {
      key: 'google',
      name: 'Google',
      url: 'https://www.google.com',
      compose: 'Opens blank — not pre-composed',
    },
    {
      key: 'gemini',
      name: 'Gemini',
      url: 'https://gemini.google.com/gems/view',
      compose: 'Opens blank — not pre-composed',
    },
    {
      key: 'wayback',
      name: 'Wayback Machine',
      url: props.focusedRecordUrl
        ? `https://web.archive.org/web/*/${props.focusedRecordUrl}`
        : 'https://web.archive.org',
      compose: props.focusedRecordUrl
        ? 'Pre-composed from the focused record’s source'
        : 'Opens blank — no record focused yet',
    },
    {
      key: 'maps',
      name: 'Google Maps',
      url: `https://www.google.com/maps/search/?api=1&query=${addressQuery}`,
      compose: 'Pre-composed from the subject’s address',
    },
    {
      key: 'linkedin',
      name: 'LinkedIn',
      url: `https://www.linkedin.com/search/results/all/?keywords=${nameQuery}`,
      compose: 'Pre-composed from the subject’s name',
    },
  ];
  if (props.accountId) {
    tools.push({
      key: 'zendesk',
      name: 'Zendesk',
      url: zendeskCardholderSearchUrl(props.accountId),
      compose: 'Pre-composed from the cardholder ID',
    });
  }
  return tools;
});

type Tab = 'tools' | 'reference' | 'documents';
// The Documents tab only exists in the mobile `sheet` variant
// It is a docked footer on desktop.
const TABS = computed<{ key: Tab; label: string }[]>(() => {
  const tabs: { key: Tab; label: string }[] = [
    { key: 'tools', label: 'Tools' },
    { key: 'reference', label: 'Reference' },
  ];
  if (props.variant === 'sheet') {
    tabs.push({
      key: 'documents',
      label: props.documentsCount != null ? `Documents ${props.documentsCount}` : 'Documents',
    });
  }
  return tabs;
});

// Loaded from a hardcoded JSON file for now, keyed by the AML review's own
// region (not the region of whichever list happened to produce a given
// hit) — one level of grouping, one level of links, rendered in exactly the
// order the file gives them.
const referenceGroups = computed(() => standardsForRegion(props.reviewRegion));

const activeTab = ref<Tab>('tools');

// Google/Gemini/Wayback/Maps/LinkedIn all block iframe embedding, so
// there's no live destination to show docked in the panel — every tool
// row opens straight into a new tab on click, matching v4's "launcher
// list" (no docked single-tool card to switch between anymore).
function selectTool(key: ToolKey) {
  const tool = TOOLS.value.find((t) => t.key === key);
  if (tool?.url) window.open(tool.url, '_blank', 'noopener');
}

function setToolByIndex(index: number) {
  const tool = TOOLS.value[index];
  if (tool) selectTool(tool.key);
}

function openReferenceTab() {
  activeTab.value = 'reference';
}

// The "R" shortcut's target — switches between Tools and Reference, the
// only two tabs this pane has now that the docked reader (Stage 6d) moved
// to its own full-pane review mode instead.
function toggleTab() {
  activeTab.value = activeTab.value === 'reference' ? 'tools' : 'reference';
}

defineExpose({ setToolByIndex, openReferenceTab, toggleTab });

const rootClass = computed(() =>
  props.variant === 'panel'
    ? 'hidden w-[332px] flex-none flex-col border-l border-gray-200 bg-white lg:flex'
    : 'flex flex-1 flex-col bg-white'
);
</script>

<template>
  <div :class="rootClass" data-test="tool-panel">
    <div class="flex flex-none gap-1 border-b border-gray-200 bg-gray-50 p-2">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        type="button"
        class="flex-1 rounded-md py-1.5 text-xs font-semibold"
        :class="activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div
      class="min-h-0 flex-1 overflow-y-auto p-3"
      :style="
        variant === 'sheet'
          ? { paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }
          : undefined
      "
    >
      <div v-if="activeTab === 'tools'" class="flex flex-col gap-2">
        <p class="mb-0.5 text-[11.5px] text-gray-500">
          Tools open in a new tab with case values pre-filled. Nothing is embedded — external sites
          block framing.
        </p>
        <button
          v-for="(tool, i) in TOOLS"
          :key="tool.key"
          type="button"
          class="flex w-full items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-left hover:border-gray-300 hover:bg-gray-50"
          @click="selectTool(tool.key)"
        >
          <span class="min-w-0 flex-1">
            <span class="flex items-center gap-2">
              <span class="text-[13px] font-semibold text-gray-900">{{ tool.name }}</span>
              <span
                v-if="variant === 'panel' && kbEnabled"
                class="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-500"
                >{{ i + 1 }}</span
              >
            </span>
            <span class="mt-1 block text-[11.5px] text-gray-500">{{ tool.compose }}</span>
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mt-1 flex-none text-gray-400"
          >
            <path d="M12 4h4v4" />
            <path d="M16 4 9 11" />
            <path d="M15 12v4H4V5h4" />
          </svg>
        </button>
      </div>

      <div v-else-if="activeTab === 'reference'" class="flex flex-col gap-4">
        <div v-for="group in referenceGroups" :key="group.name" class="flex flex-col gap-2">
          <span class="text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">{{
            group.name
          }}</span>
          <a
            v-for="item in group.items"
            :key="item.link"
            :href="item.link"
            target="_blank"
            rel="noopener noreferrer"
            class="flex flex-col gap-1 rounded-md border border-gray-200 p-3 hover:border-gray-400"
          >
            <span class="text-[12.5px] font-semibold text-gray-900">{{ item.title }}</span>
            <span class="text-[11.5px] text-gray-500">{{ item.description }}</span>
            <span class="text-[11px] text-indigo-600">{{
              item.link.replace(/^https?:\/\//, '')
            }}</span>
          </a>
        </div>
      </div>

      <div v-else class="flex flex-col">
        <slot name="documents" />
      </div>
    </div>

    <slot name="footer" />
  </div>
</template>
